'use client';

import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

const CONTENT = {
  en: {
    eyebrow: 'The program',
    heading: 'Love Open, or get every dollar back.',
    subtitle:
      "One program. One promise. Apply once and you're protected for your entire first 120 days.",
    guaranteeLabel: 'Refund Guarantee',
    refundCap: 'refund cap',
    capAmount: '$2 million',
    pitchHeading:
      "If Open isn't the best AI agent your team has ever used, we refund every dollar.",
    pitchCopy:
      "You don't have to justify it. You don't have to hit a number. If you're not 100% satisfied within your first 120 days, tell us once and the refund hits your bank account.",
    cta: 'Learn more',
    eligibilityLabel: 'Eligibility',
    eligibility: [
      'New Open customers',
      'Apply within your first 120 days of subscription',
      'Run Open on at least 500 paid conversations',
    ],
    flowLabel: 'How the refund flows',
    timeline: [
      {
        step: 'Apply',
        copy: 'Submit a 30-second application. We confirm enrollment by email.',
      },
      {
        step: 'Run Open in production',
        copy: 'Use Open on real customer traffic for up to 120 days. No metrics to hit.',
      },
      {
        step: 'Refund, no questions',
        copy: 'Not 100% satisfied? Tell us once and we wire every dollar of Open spend back, up to $2M.',
      },
    ],
    terms:
      'See the full Refund Guarantee Terms & Conditions for refund mechanics, payout timing, and eligibility specifics.',
  },
  ar: {
    eyebrow: 'البرنامج',
    heading: 'إما تحب Open، أو نرجّع لك كل ريال.',
    subtitle:
      'برنامج واحد. وعد واحد. قدّم مرة وحدة، وتصير محمي طوال أول ١٢٠ يوم.',
    guaranteeLabel: 'ضمان الاسترجاع',
    refundCap: 'حد الاسترجاع',
    capAmount: '٧,٥ مليون ⃁',
    pitchHeading:
      'إذا ما كان Open أفضل وكيل ذكي استخدمه فريقك، نرجّع لك كل ريال.',
    pitchCopy:
      'لا تبرير مطلوب. ولا رقم لازم تحققه. إذا ما كنت راضي ١٠٠٪ خلال أول ١٢٠ يوم، خبّرنا مرة وحدة ويوصل الاسترجاع لحسابك البنكي.',
    cta: 'اعرف أكثر',
    eligibilityLabel: 'الأهلية',
    eligibility: [
      'عملاء Open الجدد',
      'التقديم خلال أول ١٢٠ يوم من الاشتراك',
      'تشغيل Open على ٥٠٠ محادثة مدفوعة على الأقل',
    ],
    flowLabel: 'كيف يسير الاسترجاع',
    timeline: [
      {
        step: 'قدّم',
        copy: 'عبّي طلب من ٣٠ ثانية. نؤكّد تسجيلك عبر الإيميل.',
      },
      {
        step: 'استخدم Open مع عملائك',
        copy: 'استخدم Open على حركة عملائك الحقيقية حتى ١٢٠ يوم. بلا مؤشرات لازم توصلها.',
      },
      {
        step: 'استرجاع، بدون أسئلة',
        copy: 'مو راضي ١٠٠٪؟ خبّرنا مرة وحدة، ونحوّل لك كل ريال دفعته على Open، حتى ٧,٥ مليون ⃁.',
      },
    ],
    terms:
      'راجع شروط وأحكام ضمان الاسترجاع كاملة عشان تعرف آلية الاسترجاع، ومواعيد الصرف، وتفاصيل الأهلية.',
  },
} as const;

export const RefundProgramSection = () => {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <section className="border-t border-white/5 py-20 md:py-24">
      <div className="container">
        <div className="mb-10 max-w-2xl text-start md:mb-12">
          <p className="mb-3 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-amber-400">
            <span className="inline-flex h-1 w-1 rounded-full bg-amber-400" />
            {c.eyebrow}
          </p>
          <h2 className="font-serif text-balance text-white text-2xl md:text-3xl">
            {c.heading}
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-neutral-400 md:text-base">
            {c.subtitle}
          </p>
        </div>

        <article className="overflow-hidden rounded-2xl border border-white/10 bg-neutral-950">
          <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            {/* Left — pitch + apply */}
            <div className="border-b border-white/10 p-8 md:p-12 lg:border-b-0 lg:border-r">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">
                {c.guaranteeLabel}
              </p>

              <div className="mt-5 flex items-baseline gap-3">
                <span className="font-sans font-normal leading-none text-white text-3xl md:text-4xl">
                  {c.capAmount}
                </span>
                <span className="text-xs text-neutral-400">{c.refundCap}</span>
              </div>

              <h3 className="mt-8 font-serif text-balance text-white text-lg md:text-xl">
                {c.pitchHeading}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-400">
                {c.pitchCopy}
              </p>

              <div className="mt-8">
                <Button
                  asChild
                  className="group h-12 rounded-full bg-white px-7 text-base font-medium text-neutral-950 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(255,255,255,0.25)] hover:bg-neutral-100"
                >
                  <Link
                    href="/demo"
                    prefetch
                    aria-label={
                      t.common['Learn more about Open and book a demo']
                    }
                  >
                    {c.cta}
                    <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right — eligibility + flow */}
            <div className="p-8 md:p-12">
              <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">
                {c.eligibilityLabel}
              </p>
              <ul className="mt-5 space-y-3 text-sm text-white/85">
                {c.eligibility.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1 w-1 shrink-0 rounded-full bg-white/50"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10 border-t border-white/10 pt-8">
                <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-white/40">
                  {c.flowLabel}
                </p>
                <ol className="mt-5 space-y-5">
                  {c.timeline.map((row, i) => (
                    <li key={row.step} className="flex items-start gap-4">
                      <span className="mt-0.5 w-5 shrink-0 text-[10px] font-medium text-white/40 tabular-nums">
                        0{i + 1}
                      </span>
                      <div className="min-w-0 text-sm">
                        <div className="font-medium text-white">{row.step}</div>
                        <div className="mt-1 leading-relaxed text-neutral-400">
                          {row.copy}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </article>

        <p className="mt-6 text-xs text-neutral-500">{c.terms}</p>
      </div>
    </section>
  );
};

export default RefundProgramSection;
